import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useChainId, useAccount } from 'wagmi';
import { type PermitSingleData, type PermitBatchData } from '@uniswap/permit2-sdk';
import { type AddLiquidityParams, LiquidityAction } from '@/types';
import { type Permit } from '@/onchain/hooks/usePermit2';
import { verifiedPoolsPositionManagerAbi } from '@generated/generated';
import { PermitAndExecuteModal } from '@/components/PermitAndExecuteModal';
import { getAddLiquidityMulticallArgs } from '@/onchain/positionManager';
import { ActionType, AnalyticsEvents, ComponentType, logEvent } from '@/app/utils/analytics/events';
import { useAddLiquidityEventData } from '@/hooks/analytics/useAddLiquidityEventData';
import { isNativeToken } from '@/onchain/assets';
import { format, DisplayType } from '@/utils/numberFormatter/formatNumbers';
import { useAddLiquidityState } from '@/components/AddLiquidity/AddLiquidityProvider';
import { getPositionManagerForHook } from '@/onchain/utils/hookConfig';

type AddLiquidityConfirmationContentProps = {
  addLiquidityParams: AddLiquidityParams;
  open: boolean;
  onSuccess: (positionId?: bigint) => void;
  onClose: (event?: { positionId?: bigint }) => void;
};

/**
 * Confirmation content for add liquidity flow.
 * Wraps PermitAndExecuteModal to handle the transaction flow.
 *
 * This component is designed to be used within the add liquidity flow. It handles:
 * - Permit2 signature
 * - Contract execution
 * - Success/error callbacks
 * - Analytics logging
 *
 * Always renders inline (no modal wrapper) since this component is embedded
 * within the parent AddLiquidityLayout modal.
 */
export const AddLiquidityConfirmationContent = memo(function AddLiquidityConfirmationContent({
  addLiquidityParams,
  open,
  onSuccess,
  onClose,
}: AddLiquidityConfirmationContentProps) {
  const chainId = useChainId();
  const { address } = useAccount() as { address: `0x${string}` };

  const { setTxHash, latestPositionId, executionDeadlineDeltaSeconds } = useAddLiquidityState();

  // Track tx success state for calling onSuccess with positionId
  const [txSucceeded, setTxSucceeded] = useState(false);
  const [onSuccessCalled, setOnSuccessCalled] = useState(false);

  // Call onSuccess with positionId once it becomes available after tx success
  // - For INCREASE: addLiquidityParams.positionId is already set, so this runs immediately
  // - For MINT: latestPositionId becomes available after the query completes
  useEffect(() => {
    if (!txSucceeded || onSuccessCalled) {
      return;
    }
    const positionId = latestPositionId ?? addLiquidityParams.positionId;
    if (positionId) {
      setOnSuccessCalled(true);
      onSuccess(positionId);
    }
  }, [txSucceeded, onSuccessCalled, latestPositionId, addLiquidityParams.positionId, onSuccess]);

  const permit = useMemo((): Permit => {
    // Create array of permit details for both tokens
    // Each detail contains the amount and token address for the deposit
    const permitDetails = [
      {
        amount: addLiquidityParams.asset0DepositAmount,
        token: (addLiquidityParams.asset0?.address[chainId] as `0x${string}`) ?? '',
      },
      {
        amount: addLiquidityParams.asset1DepositAmount,
        token: (addLiquidityParams.asset1?.address[chainId] as `0x${string}`) ?? '',
      },
      // Filter out native ETH since it doesn't need a permit
      // Native ETH is handled directly through the transaction value
    ].filter((detail) => !isNativeToken(chainId, detail.token));

    // Get the correct position manager address based on the pool's hook
    // This ensures the permit is issued to the right contract (V1 or V2)
    if (!addLiquidityParams.pool) {
      throw new Error('Pool is required for add liquidity');
    }
    const positionManagerAddress = getPositionManagerForHook(
      addLiquidityParams.pool.key.hooks,
      chainId,
    );

    return {
      // Only include non-native tokens that require permits
      details: permitDetails,
      // The spender is the position manager contract that will receive the tokens
      spender: positionManagerAddress,
      expiration: Math.floor(Date.now() / 1000) + executionDeadlineDeltaSeconds,
    };
  }, [
    addLiquidityParams.asset0?.address,
    addLiquidityParams.asset0DepositAmount,
    addLiquidityParams.asset1?.address,
    addLiquidityParams.asset1DepositAmount,
    addLiquidityParams.pool,
    chainId,
    executionDeadlineDeltaSeconds,
  ]);

  const getContractExecutionParams = useCallback(
    (permitData: PermitSingleData | PermitBatchData, signature: `0x${string}`) => {
      const { data, value, positionManagerAddress } = getAddLiquidityMulticallArgs(
        chainId,
        address,
        addLiquidityParams,
        permitData,
        signature,
      );

      if (!positionManagerAddress) {
        throw new Error('Position manager address not found');
      }

      return {
        address: positionManagerAddress,
        abi: verifiedPoolsPositionManagerAbi,
        functionName: 'multicall',
        args: [data],
        value, // Use value returned from getAddLiquidityMulticallArgs
      };
    },
    [addLiquidityParams, address, chainId],
  );

  const contractExecutionDescription = useMemo(() => {
    const asset0Amount = format({
      value: addLiquidityParams.asset0DepositAmountHumanReadable,
      type: DisplayType.TokenAmount,
    });
    const asset1Amount = format({
      value: addLiquidityParams.asset1DepositAmountHumanReadable,
      type: DisplayType.TokenAmount,
    });
    const asset0Symbol = addLiquidityParams.asset0?.displaySymbol;
    const asset1Symbol = addLiquidityParams.asset1?.displaySymbol;

    switch (addLiquidityParams.liquidityAction) {
      case LiquidityAction.MINT:
        return `Minting a new position with ${asset0Amount} ${asset0Symbol} and ${asset1Amount} ${asset1Symbol}`;
      case LiquidityAction.INCREASE:
        return `Increasing liquidity in position (Id: ${addLiquidityParams.positionId}) with ${asset0Amount} ${asset0Symbol} and ${asset1Amount} ${asset1Symbol}`;
      default:
        throw new Error('Invalid liquidity action');
    }
  }, [
    addLiquidityParams.liquidityAction,
    addLiquidityParams.positionId,
    addLiquidityParams.asset0DepositAmountHumanReadable,
    addLiquidityParams.asset1DepositAmountHumanReadable,
    addLiquidityParams.asset0?.displaySymbol,
    addLiquidityParams.asset1?.displaySymbol,
  ]);

  const eventData = useAddLiquidityEventData({ addLiquidityParams, chainId });

  const handleSuccess = useCallback(
    (txHash: `0x${string}`) => {
      setTxHash(txHash);
      setTxSucceeded(true);
      logEvent(AnalyticsEvents.AddLiquiditySuccess.eventName, {
        loggingId: AnalyticsEvents.AddLiquiditySuccess.loggingId,
        action: ActionType.view,
        componentType: ComponentType.modal,
        ...eventData,
      });
    },
    [eventData, setTxHash],
  );

  const handleError = useCallback(
    (error: Error) => {
      logEvent(AnalyticsEvents.AddLiquidityError.eventName, {
        loggingId: AnalyticsEvents.AddLiquidityError.loggingId,
        action: ActionType.view,
        componentType: ComponentType.modal,
        ...eventData,
        error: error?.message,
      });
    },
    [eventData],
  );

  const handleClose = useCallback(() => {
    onClose({
      positionId: latestPositionId ?? addLiquidityParams.positionId,
    });
  }, [latestPositionId, onClose, addLiquidityParams.positionId]);

  return (
    <PermitAndExecuteModal
      inline
      open={open}
      onClose={handleClose}
      onSuccess={handleSuccess}
      onError={handleError}
      contractExecutionDescription={contractExecutionDescription}
      permit={permit}
      getContractExecutionParams={getContractExecutionParams}
      dataTestIdPrefix="add-liquidity-confirmation"
    />
  );
});
